import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import './tour.css';

const PAD = 22;     // space between the target and the spotlight edge (the edge is feathered)
const MARGIN = 16;  // space between the spotlight and the card, and from the viewport edge

// The steps. `target` is a selector (several elements are outlined together); `overlay` is
// the panel that has to be open for the target to exist, null meaning the main screen;
// `round` fits the spotlight to a circular button; `desktop` steps are skipped on phones,
// which have no table and tabbed search options.
export function tourSteps(isBrowser) {
    const steps = [
        {
            target: '[data-tour="film"]', overlay: null,
            title: 'Chosen films',
            text: 'Every trailer on Kino is for a film picked from festivals around the world. Let them play, or go looking for something.',
        },
        {
            target: '[data-tour="next"]', overlay: null, round: true,
            title: 'Next film',
            text: 'Click here to go to the next trailer.',
        },
        {
            target: '[data-tour="search"]', overlay: null, round: true,
            title: 'Search',
            text: 'Click search to see the search options.',
        },
        {
            target: '[data-tour="describe"]', overlay: 'filters', desktop: true,
            title: 'Describe a film',
            text: 'Describe the film you are after in your own words and get your results.',
        },
        {
            target: '[data-tour="genres"], [data-tour="languages"]', overlay: 'filters', desktop: true,
            title: 'Genres and languages',
            text: 'Or pick the genres or languages you are in the mood for.',
        },
        {
            target: '[data-tour="ranges"]', overlay: 'filters', desktop: true,
            title: 'Ratings, year and runtime',
            text: 'Narrow it down by ratings, year of release and runtime.',
        },
        {
            target: '[data-tour="table"]', overlay: null, round: true, desktop: true,
            title: 'Table',
            text: 'Click table to see the full table of your search.',
        },
        {
            target: '.movie-table-body tbody tr:first-child', overlay: 'movie-table', desktop: true,
            title: 'Play from the table',
            text: 'Click on a title to play that trailer.',
        },
        {
            target: '[data-tour="home"]', overlay: isBrowser ? 'movie-table' : 'filters', round: true,
            title: 'Home',
            text: 'Click home to return to the main screen.',
        },
    ];
    return isBrowser ? steps : steps.filter((s) => !s.desktop);
}

const visibleRects = (selector) =>
    Array.from(document.querySelectorAll(selector), (el) => el.getBoundingClientRect())
        .filter((r) => r.width > 0 && r.height > 0);

const union = (rects) => {
    const left = Math.min(...rects.map((r) => r.left));
    const top = Math.min(...rects.map((r) => r.top));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    return { left, top, width: right - left, height: bottom - top };
};

const sameRect = (a, b) =>
    (!a && !b) || (!!a && !!b && ['left', 'top', 'width', 'height'].every((k) => Math.abs(a[k] - b[k]) < 0.5));

// Dims the screen, cuts a spotlight around the step's target and shows a card next to it.
export default function Tour({ steps, step, onNext, onBack, onEnd }) {
    const current = steps[step];
    const last = step === steps.length - 1;
    const [rect, setRect] = useState(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const cardRef = useRef(null);

    // Keep measuring: panels open, the table loads its rows, layouts animate. While a
    // step's target hasn't rendered yet the spotlight and card stay where they were, so
    // they glide to the new element instead of jumping to the centre first. Measured
    // before paint so the first step doesn't flash in the centre either.
    useLayoutEffect(() => {
        const measure = () => {
            const rects = visibleRects(current.target);
            if (!rects.length) return;
            const next = union(rects);
            setRect((prev) => (sameRect(prev, next) ? prev : next));
        };
        measure();
        const id = setInterval(measure, 80);
        window.addEventListener('resize', measure);
        return () => {
            clearInterval(id);
            window.removeEventListener('resize', measure);
        };
    }, [current]);

    useLayoutEffect(() => {
        const el = cardRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setSize((prev) => (prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }));
    }, [step, rect]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onEnd();
            else if (e.key === 'ArrowRight' || e.key === 'Enter') (last ? onEnd : onNext)();
            else if (e.key === 'ArrowLeft' && step > 0) onBack();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [step, last, onNext, onBack, onEnd]);

    // Nothing underneath should react to clicks on the tour (the player pauses on mousedown)
    const stop = (e) => e.stopPropagation();

    let cardStyle;
    if (!rect) {
        cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
    } else {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const below = rect.top + rect.height + PAD + MARGIN + size.height <= vh;
        const top = below
            ? rect.top + rect.height + PAD + MARGIN
            : Math.max(MARGIN, rect.top - PAD - MARGIN - size.height);
        const left = Math.max(MARGIN, Math.min(rect.left, vw - size.width - MARGIN));
        cardStyle = { left, top };
    }

    return (
        <div className="tour" onClick={stop} onMouseDown={stop} onTouchStart={stop}>
            {rect ? (
                <div
                    className={`tour-spot${current.round ? ' is-round' : ''}`}
                    style={{ left: rect.left - PAD, top: rect.top - PAD, width: rect.width + 2 * PAD, height: rect.height + 2 * PAD }}
                />
            ) : (
                <div className="tour-dim" />
            )}
            <div className="tour-card" ref={cardRef} style={cardStyle} role="dialog" aria-label={`Tour, step ${step + 1} of ${steps.length}`}>
                <div className="tour-card-head">
                    <span>TOUR</span>
                    <span>{step + 1} / {steps.length}</span>
                </div>
                <p className="tour-title">{current.title}</p>
                <p className="tour-text">{current.text}</p>
                <div className="tour-actions">
                    {!last ? <button type="button" className="tour-btn tour-skip" onClick={onEnd}>SKIP</button> : null}
                    {step > 0 ? <button type="button" className="tour-btn" onClick={onBack}>BACK</button> : null}
                    <button type="button" className="tour-btn is-primary" onClick={last ? onEnd : onNext}>
                        {last ? 'FINISH' : 'NEXT'}
                    </button>
                </div>
            </div>
        </div>
    );
}
